import { useDisplayMode } from "@/context/DisplayModeContext";
import { tWithKey } from "@/lib/i18n-fallback";
import { DisplayMode } from "@/lib/display-mode";
import { cn } from "@/lib/utils";
import { CheckCircle2, ListTree, Zap } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type ModeOption = {
  value: DisplayMode;
  label: string;
  icon: ReactNode;
};

/**
 * Reusable "display mode" selector (Essentiel / Pro). Reads and writes the mode
 * from {@link useDisplayMode}, so it works identically on any sheet that mounts
 * a DisplayMode provider. Placed above the lateral tabs in Pro mode and in the
 * left column of the simplified view in Essentiel mode.
 */
export function DisplayModeCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { mode, setMode } = useDisplayMode();

  const options: ModeOption[] = [
    {
      value: "essentiel",
      label: tWithKey(t, "displayMode.essentiel", "Essentiel"),
      icon: <Zap className="h-4 w-4" aria-hidden />,
    },
    {
      value: "pro",
      label: tWithKey(t, "displayMode.pro", "Pro"),
      icon: <ListTree className="h-4 w-4" aria-hidden />,
    },
  ];

  return (
    <div
      className={cn(
        // w-full: fills the left column without widening it, so the Pro tab
        // column keeps its original (content-driven) width.
        "flex w-full flex-col gap-2 rounded-xl border border-[hsl(220_17%_93%)] bg-[hsl(210_24%_98%)] p-3",
        className,
      )}
    >
      <p className="px-1 text-xs font-bold uppercase tracking-wide text-gray-950 font-['Raleway']">
        {tWithKey(t, "displayMode.title", "Mode d'affichage")}
      </p>
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const selected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => setMode(option.value)}
              className={cn(
                "flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 text-left transition-colors",
                selected
                  ? "border-primary ring-1 ring-primary bg-blue-50/60"
                  : "border-slate-200 hover:bg-slate-50",
              )}
            >
              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                  selected
                    ? "bg-primary text-white"
                    : "bg-slate-100 text-slate-500",
                )}
              >
                {option.icon}
              </span>
              <span
                className={cn(
                  "flex-1 text-base font-semibold font-['Raleway']",
                  selected ? "text-primary" : "text-slate-700",
                )}
              >
                {option.label}
              </span>
              {selected ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden />
              ) : (
                <span
                  className="h-5 w-5 shrink-0 rounded-full border-2 border-slate-300"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default DisplayModeCard;
